/**
 * Shell execution utilities
 */

import { exec, spawn } from 'child_process';
import { promisify } from 'util';

export interface ExecResult {
  stdout: string;
  stderr: string;
}

/**
 * Execute a shell command and return the result
 *
 * @param timeoutMs - Optional timeout in milliseconds
 */
export async function run(command: string, timeoutMs?: number): Promise<ExecResult> {
  try {
    const execAsync = promisify(exec);
    const options: { timeout?: number } = {};
    if (timeoutMs && timeoutMs > 0) {
      options.timeout = timeoutMs;
    }
    const { stdout, stderr } = await execAsync(command, options);
    return { stdout: stdout.trim(), stderr: stderr.trim() };
  } catch (error) {
    const execError = error as { stdout?: string; stderr?: string; message?: string; killed?: boolean };
    if (execError.killed) {
      throw new Error(`Command timed out after ${timeoutMs}ms: ${command}`);
    }
    throw new Error(execError.stderr || execError.message || 'Unknown error');
  }
}

/**
 * Execute a command and return just stdout, throwing on error
 *
 * @param timeoutMs - Optional timeout in milliseconds
 */
export async function runOutput(command: string, timeoutMs?: number): Promise<string> {
  const { stdout } = await run(command, timeoutMs);
  return stdout;
}

/**
 * Check if a command exists
 */
export async function commandExists(command: string): Promise<boolean> {
  try {
    await run(`which ${command}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Spawn an interactive process (for SSH sessions)
 *
 * Uses shell: false to avoid DEP0190 deprecation warning.
 * Arguments are passed directly to the process without shell interpretation.
 *
 * @param timeoutMs - Optional timeout in milliseconds. If provided, the process
 *                   will be killed if it doesn't complete within the timeout.
 */
export function spawnInteractive(
  command: string,
  args: string[],
  timeoutMs?: number,
): Promise<number> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, args, {
      stdio: 'inherit',
      shell: false,
    });

    let timeoutHandle: NodeJS.Timeout | undefined;

    child.on('error', (err) => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      reject(err);
    });

    child.on('close', (code) => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      resolve(code ?? 0);
    });

    // Set up timeout if specified
    if (timeoutMs && timeoutMs > 0) {
      timeoutHandle = setTimeout(() => {
        child.kill('SIGTERM');
        // Give it 2 seconds to gracefully exit, then force kill
        setTimeout(() => child.kill('SIGKILL'), 2000);
      }, timeoutMs);
    }
  });
}
